import React, { useEffect, useRef, useState } from 'react';
import { AnimatedNumberReactProps } from 'animated-number-react';

const getEasingFn = (easing?: string) => {
  switch (easing) {
    case 'easeInQuad':
      return (t: number) => t * t;
    case 'easeOutQuad':
      return (t: number) => t * (2 - t);
    case 'easeInOutQuad':
      return (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    case 'easeOutCubic':
      return (t: number) => 1 - Math.pow(1 - t, 3);
    case 'linear':
    default:
      return (t: number) => t;
  }
};

/**
 * AnimatedNumber
 *
 * A React component that animates the transition of a number from
 * its previous value to a new value.
 */
export const AnimatedNumber: React.FC<AnimatedNumberReactProps> = ({
  className,
  delay = 200,
  duration = 150,
  easing,
  formatValue = (v) => Math.round(v),
  isPrinting = false,
  value,
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const displayValueRef = useRef(value);

  useEffect(() => {
    displayValueRef.current = displayValue;
  }, [displayValue]);

  useEffect(() => {
    if (isPrinting) {
      setDisplayValue(value);
      return;
    }

    const from = displayValueRef.current;
    const to = value;

    if (from === to) {
      return;
    }

    const easingFn = getEasingFn(easing);
    const safeDuration = Math.max(duration, 1);
    let animationFrameId = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let startTime = 0;

    const step = (timestamp: number) => {
      if (!startTime) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / safeDuration, 1);
      const easedProgress = easingFn(progress);
      const nextValue = from + (to - from) * easedProgress;

      displayValueRef.current = nextValue;
      setDisplayValue(nextValue);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step);
      }
    };

    const startAnimation = () => {
      animationFrameId = requestAnimationFrame(step);
    };

    if (delay > 0) {
      timeoutId = setTimeout(startAnimation, delay);
    } else {
      startAnimation();
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      cancelAnimationFrame(animationFrameId);
    };
  }, [delay, duration, easing, isPrinting, value]);

  // If the component is being rendered for printing, we skip the
  // animation and just display the formatted value.
  if (isPrinting) {
    return <span className={className}>{formatValue(value)}</span>;
  }

  return <span className={className}>{formatValue(displayValue)}</span>;
};

AnimatedNumber.displayName = 'AnimatedNumber';

export default AnimatedNumber;
